import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, X, CheckCircle2, ArrowUpRight, CalendarClock, FolderOpen } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { BArecordMark } from "@/components/BArecordMark";
import { track } from "@/lib/analytics";
import api, { formatApiError } from "@/lib/api";

const SERVICES = [
  "Cybersecurity Baseline Review ($499)",
  "HIPAA Security Risk Assessment",
  "Cybersecurity Assessment",
  "Cloud Consulting",
  "AI Integration",
  "Compliance Consulting",
  "Application Security",
  "Managed Security Services",
  "Technology Strategy",
];

const SLOTS_FALLBACK = ["09:00", "10:30", "12:00", "13:30", "15:00", "16:30"];

export default function ClientPortal() {
  const [params] = useSearchParams();
  const initialService = params.get("service") === "baseline-review" ? SERVICES[0] : SERVICES[1];
  const [form, setForm] = useState({ service: initialService, date: "", time_slot: "", company: "", message: "" });
  const startedRef = useRef(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ slots: SLOTS_FALLBACK, min_date: "", lead_time_months: 3 });

  const load = async () => {
    try {
      const [appts, cfg] = await Promise.all([
        api.get("/appointments/me"),
        api.get("/booking/settings"),
      ]);
      setAppointments(appts.data);
      setSettings(cfg.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => {
    if ((k === "date" || k === "time_slot") && !startedRef.current) {
      startedRef.current = true;
      track("booking_started", "/client");
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const book = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time_slot) {
      toast.error("Pick a date and time slot.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      toast.success("Consultation booked. We'll confirm by email.");
      setForm({ service: SERVICES[1], date: "", time_slot: "", company: "", message: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Appointment cancelled.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const active = appointments.filter((a) => a.status !== "cancelled");
  const nextAppt = [...active].sort((a, b) => (a.date + a.time_slot).localeCompare(b.date + b.time_slot))[0];
  const completed = appointments.filter((a) => a.status === "completed").length;

  return (
    <PortalShell
      eyebrow="Client Portal"
      title="Your security workspace"
      subtitle="Book consultations, track your engagements, and access Aliwer products — all in one place. Free consults are typically scheduled within 12 weeks."
    >
      {/* Overview row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-10" data-testid="client-overview">
        <div className="rounded-2xl border border-white/10 bg-surface p-6" data-testid="client-next-consult">
          <CalendarClock size={18} className="text-fog" />
          <p className="mt-3 text-xs uppercase tracking-widest text-fog">Next consultation</p>
          {nextAppt ? (
            <>
              <p className="mt-2 font-display text-lg tracking-tight text-paper">{nextAppt.service}</p>
              <p className="mt-1 text-sm text-fog">
                {nextAppt.date} · {nextAppt.time_slot} <span className="text-fog/70">CT</span> ·{" "}
                <span className="uppercase text-xs tracking-widest text-paper">{nextAppt.status}</span>
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-fog">Nothing scheduled yet — book your free consult below.</p>
          )}
        </div>
        <div className="rounded-2xl border border-white/10 bg-surface p-6" data-testid="client-engagement-status">
          <FolderOpen size={18} className="text-fog" />
          <p className="mt-3 text-xs uppercase tracking-widest text-fog">Engagement status</p>
          <p className="mt-2 font-display text-lg tracking-tight text-paper">
            {active.length} upcoming · {completed} completed
          </p>
          <p className="mt-1 text-sm text-fog">Reports and documents from engagements are delivered by email.</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-surface p-6 sm:col-span-2 lg:col-span-1" data-testid="client-quick-links">
          <p className="text-xs uppercase tracking-widest text-fog mb-3">Your Aliwer products</p>
          <div className="space-y-2">
            <Link to="/barecord" data-testid="client-link-barecord" className="group flex items-center justify-between rounded-xl border border-white/10 px-4 py-2.5 hover:bg-white/5 transition-colors">
              <span className="flex items-center gap-2 text-sm text-paper"><BArecordMark size={14} /> BArecord</span>
              <ArrowUpRight size={14} className="text-fog transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/products/securedesk" data-testid="client-link-securedesk" className="group flex items-center justify-between rounded-xl border border-white/10 px-4 py-2.5 hover:bg-white/5 transition-colors">
              <span className="text-sm text-paper">SecureDesk <span className="text-[9px] uppercase tracking-widest text-fog ml-1">Waitlist · Nov 7</span></span>
              <ArrowUpRight size={14} className="text-fog transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/services" data-testid="client-link-services" className="group flex items-center justify-between rounded-xl border border-white/10 px-4 py-2.5 hover:bg-white/5 transition-colors">
              <span className="text-sm text-paper">Explore services &amp; pricing</span>
              <ArrowUpRight size={14} className="text-fog transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
        {/* Booking form */}
        <form onSubmit={book} className="rounded-2xl border border-white/10 bg-surface p-8" data-testid="booking-form">
          <h2 className="font-display text-xl tracking-tight text-paper mb-1">Book your free 20-minute consult</h2>
          <p className="text-xs text-fog mb-6">No charge, no commitment — typically scheduled within 12 weeks.</p>

          <label className="text-xs uppercase tracking-widest text-fog">Service</label>
          <select
            value={form.service}
            onChange={(e) => set("service", e.target.value)}
            data-testid="booking-service"
            className="mt-2 mb-5 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
          >
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-black">
                {s}
              </option>
            ))}
          </select>

          <label htmlFor="booking-date-input" className="text-xs uppercase tracking-widest text-fog">Date</label>
          <div className="relative">
            <input
              id="booking-date-input"
              type="date"
              min={settings.min_date}
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
              aria-label="Select a consultation date"
              data-testid="booking-date"
              className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors [color-scheme:dark]"
            />
            {!form.date && (
              <span className="pointer-events-none absolute left-4 top-[1.35rem] text-sm text-fog/70 sm:hidden" data-testid="booking-date-placeholder">
                Select a date
              </span>
            )}
          </div>
          {settings.min_date && (
            <p className="mt-2 mb-5 text-xs text-fog" data-testid="booking-earliest-note">
              Earliest available date: <span className="text-paper">{settings.min_date}</span> — consults are typically scheduled within 12 weeks.
            </p>
          )}

          <label className="text-xs uppercase tracking-widest text-fog">
            Time slot <span className="normal-case tracking-normal text-fog/70">— Central Time (CT)</span>
          </label>
          <div className="mt-2 mb-5 grid grid-cols-3 gap-2">
            {settings.slots.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => set("time_slot", s)}
                data-testid={`slot-${s}`}
                className={`rounded-xl border py-2.5 text-sm transition-colors ${
                  form.time_slot === s
                    ? "bg-paper text-black border-paper"
                    : "border-white/15 text-fog hover:text-paper"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <label className="text-xs uppercase tracking-widest text-fog">Notes (optional)</label>
          <textarea
            rows={3}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            data-testid="booking-message"
            className="mt-2 mb-6 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors resize-none"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="w-full rounded-full bg-paper text-black text-sm font-medium py-3.5 hover:bg-white transition-colors disabled:opacity-60"
          >
            {loading ? "Booking…" : "Confirm consultation"}
          </button>
        </form>

        {/* Appointments list */}
        <div>
          <h2 className="font-display text-xl tracking-tight text-paper mb-6">Your consultations</h2>
          {appointments.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-fog" data-testid="no-appointments">
              No consultations yet. Book your first one on the left.
            </div>
          ) : (
            <div className="space-y-3" data-testid="appointments-list">
              {appointments.map((a) => (
                <div
                  key={a.id}
                  className="rounded-2xl border border-white/10 bg-surface p-5"
                  data-testid={`appointment-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-display text-lg tracking-tight text-paper">{a.service}</p>
                      <div className="mt-2 flex items-center gap-4 text-sm text-fog">
                        <span className="flex items-center gap-1.5">
                          <Calendar size={14} /> {a.date}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock size={14} /> {a.time_slot} CT
                        </span>
                      </div>
                      {a.message && <p className="mt-3 text-sm text-fog/80">{a.message}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      {a.status === "cancelled" ? (
                        <span className="text-xs uppercase tracking-widest text-red-400">Cancelled</span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-paper">
                          <CheckCircle2 size={14} /> {a.status}
                        </span>
                      )}
                      {a.status !== "cancelled" && (
                        <button
                          onClick={() => cancel(a.id)}
                          data-testid={`cancel-${a.id}`}
                          className="flex items-center gap-1 text-xs text-fog hover:text-red-400 transition-colors"
                        >
                          <X size={12} /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
